import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import { KNOWLEDGE_BASE_DATA } from "./knowledgeBaseData";

const KBVideoGuide = ({ src, poster, alt = "Video Guide" }) => {
  const isMp4 = typeof src === "string" && src.toLowerCase().includes(".mp4");
  const validPoster = poster && !poster.toLowerCase().includes(".mp4") ? poster : undefined;

  if (isMp4) {
    return (
      <div className="kb-video-media-container">
        <video
          controls
          playsInline
          preload="metadata"
          poster={validPoster}
          src={src}
          className="kb-responsive-video"
        >
          <source src={src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  return (
    <div className="kb-video-media-container">
      <div className="kb-video-poster-wrapper">
        <img
          src={validPoster || src}
          alt={alt}
          className="kb-responsive-img"
        />
      </div>
    </div>
  );
};

const KnowledgeBaseContent = ({ activeArticleId }) => {
  const activeArticleData = useMemo(() => {
    let found = null;
    let foundBreadcrumbs = {};

    KNOWLEDGE_BASE_DATA.forEach((moduleItem) => {
      moduleItem.topics.forEach((topic) => {
        topic.groups.forEach((group) => {
          group.items.forEach((item) => {
            if (item.id === activeArticleId) {
              found = item;
              foundBreadcrumbs = {
                module: moduleItem.title,
                moduleDescription: moduleItem.description,
                topic: topic.title,
                topicDescription: topic.description,
                group: group.title,
              };
            }
          });
        });
      });
    });

    // Default fallback to first article if not found
    if (!found) {
      const defaultModule = KNOWLEDGE_BASE_DATA[0];
      const defaultTopic = defaultModule.topics[0];
      const defaultGroup = defaultTopic.groups[0];
      found = defaultGroup.items[0];
      foundBreadcrumbs = {
        module: defaultModule.title,
        moduleDescription: defaultModule.description,
        topic: defaultTopic.title,
        topicDescription: defaultTopic.description,
        group: defaultGroup.title,
      };
    }

    return { article: found, breadcrumbs: foundBreadcrumbs };
  }, [activeArticleId]);

  const { article, breadcrumbs } = activeArticleData;

  // Determine single workflow string to display (prevent yellow + blue duplicate)
  const displayWorkflow = article.workflow || 
    article.details?.find((d) => d.type === "workflow")?.content;

  return (
    <div className="kb-content-container">
      <div className="kb-content-card">
        {/* Breadcrumbs */}
        <Breadcrumbs className="kb-breadcrumbs" aria-label="breadcrumb">
          <Link underline="hover" color="inherit" href="#">
            Knowledge Base
          </Link>
          <Typography color="text.secondary">{breadcrumbs.module}</Typography>
          {breadcrumbs.topic !== breadcrumbs.group ? (
            <>
              <Typography color="text.secondary">{breadcrumbs.topic}</Typography>
              <Typography color="primary" sx={{ fontWeight: 600 }}>
                {breadcrumbs.group}
              </Typography>
            </>
          ) : (
            <Typography color="primary" sx={{ fontWeight: 600 }}>
              {breadcrumbs.topic}
            </Typography>
          )}
        </Breadcrumbs>

        {/* Title */}
        <h1 className="kb-article-title">{article.title}</h1>

        {/* Module Description Subtitle */}
        {breadcrumbs.moduleDescription && (
          <Typography
            className="kb-module-description"
            sx={{
              fontSize: "15px",
              color: "#475569",
              lineHeight: 1.6,
              marginBottom: "18px",
            }}
          >
            {breadcrumbs.moduleDescription}
          </Typography>
        )}

        {/* Single Prominent Workflow Chip (Only One Displayed) */}
        {displayWorkflow && (
          <div className="kb-workflow-chip">
            <AccountTreeIcon sx={{ color: "#FDB913" }} />
            <div>
              <span style={{ fontSize: "12px", color: "#667085", display: "block" }}>
                WORKFLOW
              </span>
              <span>{displayWorkflow}</span>
            </div>
          </div>
        )}

        {/* Article Details */}
        {article.details?.map((section, idx) => {
          if (section.type === "text") {
            return (
              <Typography
                key={idx}
                paragraph
                sx={{ fontSize: "15px", color: "#334155", lineHeight: 1.6 }}
              >
                {section.content}
              </Typography>
            );
          }

          if (section.type === "heading") {
            const isVideoHeading = (section.title || section.content || "")
              .toLowerCase()
              .includes("video");
            return (
              <div key={idx} className="kb-section-title" style={{ marginTop: "24px" }}>
                {isVideoHeading ? (
                  <OndemandVideoIcon sx={{ color: "#0054A6" }} />
                ) : (
                  <InfoOutlinedIcon sx={{ color: "#0054A6" }} />
                )}
                <span>{section.title || section.content}</span>
              </div>
            );
          }

          if (section.type === "image") {
            return (
              <div key={idx} className="kb-image-wrapper">
                <img
                  src={section.src}
                  alt={section.alt || "Vitals"}
                  className="kb-responsive-img"
                  loading="lazy"
                />
              </div>
            );
          }

          if (section.type === "video") {
            return (
              <div key={idx} style={{ marginTop: section.title ? "24px" : "12px", marginBottom: "20px" }}>
                {section.title && (
                  <div className="kb-section-title">
                    <OndemandVideoIcon sx={{ color: "#0054A6" }} />
                    <span>{section.title}</span>
                  </div>
                )}
                <KBVideoGuide src={section.src} poster={section.poster} alt={section.alt || "Video Guide"} />
              </div>
            );
          }

          // Skip section.type === "workflow" here to avoid yellow + blue duplication
          if (section.type === "workflow") {
            return null;
          }

          if (section.type === "fields" || section.type === "options") {
            return (
              <div key={idx} style={{ marginTop: "24px" }}>
                <div className="kb-section-title">
                  <InfoOutlinedIcon sx={{ color: "#0054A6" }} />
                  <span>{section.title}</span>
                </div>
                {section.items.map((field, fIdx) => (
                  <div key={fIdx} className="kb-field-item">
                    <div className="kb-field-name">{field.name}</div>
                    <div className="kb-field-desc">{field.desc}</div>
                    {field.helper && (
                      <div className="kb-field-helper">
                        Helper Text: {field.helper}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          }

          if (section.type === "buttons") {
            return (
              <div key={idx} style={{ marginTop: "24px" }}>
                <div className="kb-section-title">
                  <TouchAppIcon sx={{ color: "#0054A6" }} />
                  <span>{section.title}</span>
                </div>
                {section.items.map((btn, bIdx) => (
                  <div key={bIdx} className="kb-field-item">
                    <div className="kb-field-name">
                      <Chip
                        label={btn.name}
                        size="small"
                        sx={{
                          backgroundColor: "#0054A6",
                          color: "#FFFFFF",
                          fontWeight: 600,
                          mr: 1,
                        }}
                      />
                    </div>
                    <div className="kb-field-desc">{btn.desc}</div>
                  </div>
                ))}
              </div>
            );
          }

          if (section.type === "notification" || section.type === "systemAction") {
            return (
              <div key={idx} className="kb-notification-box">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: 700,
                    marginBottom: "6px",
                  }}
                >
                  <CheckCircleOutlineIcon />
                  <span>{section.title}</span>
                </div>
                <div style={{ whiteSpace: "pre-line" }}>{section.content}</div>
              </div>
            );
          }

          return null;
        })}

        {/* Extra Bottom Clearance Spacer (Prevents content sticking to card bottom) */}
        <div style={{ height: "60px" }} />
      </div>
    </div>
  );
};

export default KnowledgeBaseContent;
